import React from 'react';
import {
	Brain,
	Volume2,
	ChevronDown,
	Settings2,
	Target,
	Headphones,
	Wand2,
	Info,
	AlertTriangle
} from 'lucide-react';

const TTSSection = ({
	instanceId,
	settings,
	onSettingsChange,
	showTTSSettings,
	toggleTTSSettings,
	setShowSmartAudioModal,
	setShowElevenLabsModal,
	knowledgeBaseActive
}) => {
	return (
		<div className="relative overflow-hidden bg-gradient-to-br from-emeraldPrimary/25 via-cyanPrimary/20 to-tealPrimary/25 rounded-xl border border-emeraldMedium/50 shadow-lg hover:shadow-emeraldPrimary/25 transition-shadow duration-200">
			<button
				type="button"
				className="relative z-10 flex w-full items-center justify-between p-4 cursor-pointer bg-transparent text-left"
				onClick={toggleTTSSettings}
				aria-expanded={showTTSSettings}
			>
				<h3 className="font-medium flex items-center">
					<div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emeraldPrimary/40 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-emeraldMedium/60 mr-3">
						<Volume2 className="h-4 w-4 text-emeraldLight" />
					</div>
					<span className="text-textPrimary">Audio com IA</span>
				</h3>
				<div
					className={`transform transition-transform duration-200 ${showTTSSettings ? "rotate-180" : ""}`}
				>
					<ChevronDown
						className="h-5 w-5 text-textPrimary"
						aria-hidden="true"
					/>
				</div>
			</button>

			<div
				className={`transition-all duration-300 ease-in-out ${showTTSSettings ? "max-h-[1200px] opacity-100 overflow-y-auto" : "max-h-0 opacity-0 overflow-hidden"}`}
			>
				<div className="px-4 pb-4">
					<div className="mb-4 p-3 bg-emeraldPrimary/10 border border-emeraldPrimary/30 rounded-lg">
						<div>
							<h4 className="text-textPrimary font-semibold text-sm mb-2">
								Como Funciona o Audio com IA
							</h4>
							<div className="space-y-1 text-xs text-textSecondary mb-3">
								<div className="flex items-start gap-2">
									<span className="text-emeraldMedium mt-0.5">1️⃣</span>
									<span>Habilitar Audio com IA apenas permite usar áudio (não muda comportamento)</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-emeraldMedium mt-0.5">2️⃣</span>
									<span>Para usar áudio, ative uma das opções: Substituir, Inteligente ou Resposta Auto</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-emeraldMedium mt-0.5">3️⃣</span>
									<span>Sem opções ativas = apenas texto (mesmo com Audio com IA habilitado)</span>
								</div>
							</div>
						</div>
					</div>

					<div className="relative z-10 bg-grayBase/80 rounded-lg p-5 space-y-5 shadow-inner border border-emeraldMedium/20 backdrop-blur-sm">
						<div className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-emeraldPrimary/25">
							<div className="flex items-center gap-3">
								<div>
									<label className="text-sm font-medium text-textPrimary">
										Habilitar Audio com IA
									</label>
									<p className="text-xs text-grayText">
										Habilita a capacidade de usar áudio (não altera comportamento automaticamente)
									</p>
								</div>
							</div>
							<label className="relative inline-flex items-center cursor-pointer">
								<input
									type="checkbox"
									name="TTS_ENABLED"
									checked={settings.TTS_ENABLED}
									onChange={onSettingsChange}
									className="sr-only peer"
								/>
								<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
							</label>
						</div>

						{settings.TTS_ENABLED && (
							<div className="space-y-6">
								<div className="bg-gradient-to-br from-bluePrimary/10 via-purplePrimary/5 to-indigoPrimary/10 backdrop-blur-xl rounded-xl border border-blueSecondary/30 p-5 shadow-lg">
									<div className="flex items-center gap-3 mb-4">
										<div className="p-2 rounded-full bg-bluePrimary/20 border border-blueSecondary/40">
											<Volume2 className="h-5 w-5 text-blueTertiary" />
										</div>
										<div>
											<h3 className="text-white font-semibold text-lg">Escolha de Voz</h3>
											<p className="text-blueTertiary/70 text-sm">Selecione o provedor e a voz que será utilizada</p>
										</div>
									</div>

									<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
										<div className={`relative p-4 rounded-xl border transition-all duration-300 ${
											!settings.ELEVENLABS_ENABLED
												? 'bg-gradient-to-br from-emeraldBase/20 to-cyanPrimary/20 border-emeraldMedium/50 shadow-emeraldMedium/20 shadow-lg'
												: 'bg-grayContent/40 border-grayLight/30 opacity-60'
										}`}>
											<div className="flex items-start justify-between mb-3">
												<div className="flex items-center gap-2">
													<div className={`p-1.5 rounded-lg ${!settings.ELEVENLABS_ENABLED ? 'bg-emeraldBase/30' : 'bg-grayLight/30'}`}>
														<Brain className={`h-4 w-4 ${!settings.ELEVENLABS_ENABLED ? 'text-emeraldLight' : 'text-grayText'}`} />
													</div>
													<div>
														<h4 className={`font-medium text-sm ${!settings.ELEVENLABS_ENABLED ? 'text-white' : 'text-grayText'}`}>
															Voz Nativa {settings.AI_SELECTED === "PUREROUTER" ? "oZap AI" : settings.AI_SELECTED}
														</h4>
														<p className={`text-xs ${!settings.ELEVENLABS_ENABLED ? 'text-emeraldLight/70' : 'text-graySubtle'}`}>
															{settings.AI_SELECTED === "GPT" && "Vozes OpenAI incluídas"}
															{settings.AI_SELECTED === "GEMINI" && "Audio nativo do Gemini"}
															{settings.AI_SELECTED === "PUREROUTER" && "Vozes em português (Amazon Polly)"}
														</p>
													</div>
												</div>
												{!settings.ELEVENLABS_ENABLED && (
													<span className="px-2 py-0.5 text-xs bg-emeraldBase/20 text-emeraldLight rounded-full border border-emeraldBase/40">
														ATIVO
													</span>
												)}
											</div>

											{!settings.ELEVENLABS_ENABLED && (
												<div className="relative">
													{settings.AI_SELECTED === "GPT" && (
														<select
															name="OPENAI_TTS_VOICE"
															value={settings.OPENAI_TTS_VOICE}
															onChange={onSettingsChange}
															className="w-full rounded-lg border border-emeraldMedium/30 bg-emeraldDeep/30 px-3 py-2.5 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-sm appearance-none cursor-pointer text-sm"
														>
															<option value="alloy" className="bg-slateCard text-white">Alloy (Neutro)</option>
															<option value="echo" className="bg-slateCard text-white">Echo (Masculino)</option>
															<option value="fable" className="bg-slateCard text-white">Fable (Britânico)</option>
															<option value="onyx" className="bg-slateCard text-white">Onyx (Profundo)</option>
															<option value="nova" className="bg-slateCard text-white">Nova (Feminino)</option>
															<option value="shimmer" className="bg-slateCard text-white">Shimmer (Suave)</option>
														</select>
													)}

													{settings.AI_SELECTED === "PUREROUTER" && (
															<select
																name="PUREROUTER_POLLY_VOICE"
																value={settings.PUREROUTER_POLLY_VOICE || "Camila"}
																onChange={onSettingsChange}
																className="w-full rounded-lg border border-emeraldMedium/30 bg-emeraldDeep/30 px-3 py-2.5 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-sm appearance-none cursor-pointer text-sm"
															>
																<option value="Camila" className="bg-slateCard text-white">Camila (Feminino)</option>
																<option value="Vitoria" className="bg-slateCard text-white">Vitória (Feminino)</option>
																<option value="Thiago" className="bg-slateCard text-white">Thiago (Masculino)</option>
																<option value="Ricardo" className="bg-slateCard text-white">Ricardo (Masculino)</option>
															</select>
														)}

														{settings.AI_SELECTED === "GEMINI" && (
														<select
															name="GEMINI_TTS_VOICE"
															value={settings.GEMINI_TTS_VOICE}
															onChange={onSettingsChange}
															className="w-full rounded-lg border border-emeraldMedium/30 bg-emeraldDeep/30 px-3 py-2.5 text-white focus:border-emeraldMedium focus:ring focus:ring-emeraldMedium/30 shadow-sm appearance-none cursor-pointer text-sm"
														>
															<option value="kore" className="bg-slateCard text-white">Kore (Padrão)</option>
															<option value="zephyr" className="bg-slateCard text-white">Zephyr (Suave)</option>
															<option value="puck" className="bg-slateCard text-white">Puck (Jovem)</option>
															<option value="charon" className="bg-slateCard text-white">Charon (Grave)</option>
															<option value="fenrir" className="bg-slateCard text-white">Fenrir (Forte)</option>
															<option value="leda" className="bg-slateCard text-white">Leda (Feminino)</option>
															<option value="aoede" className="bg-slateCard text-white">Aoede (Musical)</option>
															<option value="achernar" className="bg-slateCard text-white">Achernar (Cristalina)</option>
															<option value="achird" className="bg-slateCard text-white">Achird (Serena)</option>
															<option value="algenib" className="bg-slateCard text-white">Algenib (Dinâmica)</option>
															<option value="algieba" className="bg-slateCard text-white">Algieba (Expressiva)</option>
															<option value="alnilam" className="bg-slateCard text-white">Alnilam (Harmoniosa)</option>
															<option value="autonoe" className="bg-slateCard text-white">Autonoe (Melodiosa)</option>
															<option value="callirrhoe" className="bg-slateCard text-white">Callirrhoe (Elegante)</option>
															<option value="despina" className="bg-slateCard text-white">Despina (Calma)</option>
															<option value="enceladus" className="bg-slateCard text-white">Enceladus (Radiante)</option>
															<option value="erinome" className="bg-slateCard text-white">Erinome (Encantadora)</option>
															<option value="gacrux" className="bg-slateCard text-white">Gacrux (Brilhante)</option>
															<option value="iapetus" className="bg-slateCard text-white">Iapetus (Majestoso)</option>
															<option value="laomedeia" className="bg-slateCard text-white">Laomedeia (Misteriosa)</option>
															<option value="orus" className="bg-slateCard text-white">Orus (Fluente)</option>
															<option value="pulcherrima" className="bg-slateCard text-white">Pulcherrima (Heroica)</option>
															<option value="rasalgethi" className="bg-slateCard text-white">Rasalgethi (Luminosa)</option>
															<option value="sadachbia" className="bg-slateCard text-white">Sadachbia (Maternal)</option>
															<option value="sadaltager" className="bg-slateCard text-white">Sadaltager (Nobre)</option>
															<option value="schedar" className="bg-slateCard text-white">Schedar (Lunar)</option>
															<option value="sulafat" className="bg-slateCard text-white">Sulafat (Poderosa)</option>
															<option value="umbriel" className="bg-slateCard text-white">Umbriel (Estelar)</option>
															<option value="vindemiatrix" className="bg-slateCard text-white">Vindemiatrix (Vibrante)</option>
															<option value="zubenelgenubi" className="bg-slateCard text-white">Zubenelgenubi (Profunda)</option>
														</select>
													)}

													<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
														<ChevronDown className="h-4 w-4 text-emeraldLight" />
													</div>
												</div>
											)}
										</div>

										<div className={`relative p-4 rounded-xl border transition-all duration-300 ${
											settings.ELEVENLABS_ENABLED
												? 'bg-gradient-to-br from-purplePrimary/20 to-pinkPrimary/20 border-purpleTertiary/50 shadow-purpleTertiary/20 shadow-lg'
												: 'bg-grayContent/40 border-grayLight/30 hover:border-purplePrimary/30 hover:bg-grayContent/60 cursor-pointer'
										}`}
										onClick={() => !settings.ELEVENLABS_ENABLED && onSettingsChange({target: {name: 'ELEVENLABS_ENABLED', type: 'checkbox', checked: true}})}
										>
											<div className="flex items-start justify-between mb-3">
												<div className="flex items-center gap-2">
													<div className={`p-1.5 rounded-lg ${settings.ELEVENLABS_ENABLED ? 'bg-purplePrimary/30' : 'bg-grayLight/30'}`}>
														<Volume2 className={`h-4 w-4 ${settings.ELEVENLABS_ENABLED ? 'text-purpleText' : 'text-grayText'}`} />
													</div>
													<div>
														<h4 className={`font-medium text-sm flex items-center gap-2 ${settings.ELEVENLABS_ENABLED ? 'text-white' : 'text-graySubtle'}`}>
															ElevenLabs Premium
															<span className="text-purpleText text-xs">🎵</span>
														</h4>
														<p className={`text-xs ${settings.ELEVENLABS_ENABLED ? 'text-purpleText/70' : 'text-graySubtle'}`}>
															Qualidade de áudio superior
														</p>
													</div>
												</div>
												<div className="flex items-center gap-2">
													{settings.ELEVENLABS_ENABLED && (
														<>
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	setShowElevenLabsModal(true);
																}}
																className="p-1.5 rounded-lg bg-purplePrimary/20 hover:bg-purplePrimary/30 transition-all duration-200 border border-purpleTertiary/40"
																title="Configurações ElevenLabs"
															>
																<Settings2 className="h-3.5 w-3.5 text-purpleText" />
															</button>
															<span className="px-2 py-0.5 text-xs bg-purplePrimary/20 text-purpleText rounded-full border border-purplePrimary/40">
																ATIVO
															</span>
														</>
													)}
													<label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
														<input
															type="checkbox"
															name="ELEVENLABS_ENABLED"
															checked={settings.ELEVENLABS_ENABLED}
															onChange={onSettingsChange}
															className="sr-only peer"
														/>
														<div className="w-9 h-5 bg-grayLight peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purpleTertiary/25 rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-purplePrimary peer-checked:to-pinkPrimary"></div>
													</label>
												</div>
											</div>

											{!settings.ELEVENLABS_ENABLED && (
												<p className="text-xs text-grayText text-center py-2 border border-dashed border-grayLight rounded-lg">
													Clique para ativar vozes premium
												</p>
											)}
										</div>
									</div>
								</div>

								<div className="flex items-center gap-4">
									<div className="flex-1 h-px bg-gradient-to-r from-transparent via-emeraldMedium/30 to-transparent"></div>
									<div className="flex items-center gap-2 px-3 py-1.5 bg-emeraldBase/10 border border-emeraldMedium/30 rounded-full">
										<Settings2 className="h-4 w-4 text-emeraldLight" />
										<span className="text-emeraldLight text-sm font-medium">Configurações de Envio</span>
									</div>
									<div className="flex-1 h-px bg-gradient-to-r from-transparent via-emeraldMedium/30 to-transparent"></div>
								</div>

								<div className="space-y-4">
									<div className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-emeraldBase/25">
										<div className="flex items-center gap-3">
											<Target className="h-5 w-5 text-emeraldLight" />
											<div>
												<label className="text-sm font-medium text-textPrimary">
													Substituir Texto por Áudio
												</label>
												<p className="text-xs text-grayText">
													Se habilitado, envia apenas áudio. Se desabilitado, envia texto + áudio
												</p>
											</div>
										</div>
										<label className="relative inline-flex items-center cursor-pointer">
											<input
												type="checkbox"
												name="TTS_REPLACE_TEXT"
												checked={settings.TTS_REPLACE_TEXT}
												onChange={onSettingsChange}
												className="sr-only peer"
											/>
											<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
										</label>
									</div>

									<div className="flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-emeraldBase/25">
										<div className="flex items-center gap-3">
											<Headphones className="h-5 w-5 text-emeraldLight" />
											<div>
												<label className="text-sm font-medium text-textPrimary">
													Resposta Automática com Áudio
												</label>
												<p className="text-xs text-grayText">
													Quando receber uma mensagem de áudio, responde automaticamente com áudio
												</p>
											</div>
										</div>
										<label className="relative inline-flex items-center cursor-pointer">
											<input
												type="checkbox"
												name="AUDIO_RESPONSE_AUTO"
												checked={settings.AUDIO_RESPONSE_AUTO}
												onChange={onSettingsChange}
												className="sr-only peer"
											/>
											<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep"></div>
										</label>
									</div>

									<div className={`p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border transition-all duration-200 ${
										settings.ENVIO_EM_BLOCO
											? 'border-redBase/30 bg-redBase/5'
											: 'border-emeraldBase/25'
									}`}>
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-3">
												{settings.ENVIO_EM_BLOCO && (
													<span className="text-redMedium text-sm">⚠️</span>
												)}
												<Brain className={`h-5 w-5 ${settings.ENVIO_EM_BLOCO ? 'text-graySubtle' : 'text-emeraldLight'}`} />
												<div className="flex-1">
													<div className="flex items-center gap-2 mb-1">
														<label className={`text-sm font-medium ${settings.ENVIO_EM_BLOCO ? 'text-graySubtle' : 'text-textPrimary'}`}>
															Áudio Inteligente
														</label>
														{settings.ENVIO_EM_BLOCO && (
															<span className="px-2 py-0.5 text-xs bg-redBase/20 text-redLight rounded-full border border-redBase/40">
																BLOQUEADA
															</span>
														)}
													</div>
													<p className={`text-xs ${settings.ENVIO_EM_BLOCO ? 'text-grayLight' : 'text-grayText'}`}>
														{settings.ENVIO_EM_BLOCO
															? '⚠️ Incompatível com "Enviar resposta em uma mensagem"'
															: 'A IA escolhe apenas 1 bloco com mais de X caracteres para enviar como áudio'
														}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-3">
												<button
													type="button"
													onClick={() => setShowSmartAudioModal(true)}
													className="p-2 rounded-full bg-emeraldBase/20 hover:bg-emeraldBase/30 transition-colors duration-200 border border-emeraldBase/40"
													title="Configurações do Áudio Inteligente"
												>
													<Settings2 className="h-4 w-4 text-emeraldLight" />
												</button>
												<label className={`relative inline-flex items-center ${settings.ENVIO_EM_BLOCO ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
													<input
														type="checkbox"
														name="TTS_SMART_AUDIO"
														checked={settings.TTS_SMART_AUDIO}
														onChange={onSettingsChange}
														disabled={settings.ENVIO_EM_BLOCO}
														className="sr-only peer"
													/>
													<div className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${
														settings.ENVIO_EM_BLOCO
															? 'bg-grayMedium peer-checked:bg-grayLight'
															: 'bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 peer-checked:bg-emeraldDeep'
													}`}></div>
												</label>
											</div>
										</div>
										{settings.ENVIO_EM_BLOCO && (
											<div className="mt-3 text-xs text-redMedium flex items-center gap-1 bg-redBase/10 p-2 rounded border border-redBase/20">
												<span>🚫</span>
												<span>Desabilitado: conflita com "Enviar resposta em uma mensagem"</span>
											</div>
										)}
									</div>

									<div className={`flex items-center justify-between p-4 bg-whiteBase/5 backdrop-blur-xl rounded-lg border border-emeraldBase/25 ${knowledgeBaseActive ? 'opacity-60' : ''}`}>
										<div className="flex items-center gap-3">
											<Wand2 className="h-5 w-5 text-emeraldLight" />
											<div>
												<label className="text-sm font-medium text-textPrimary">
													Áudio como Ferramenta
												</label>
												<p className="text-xs text-grayText">
													A IA pode decidir quando enviar áudio baseado na solicitação do usuário
												</p>
											</div>
										</div>
										<label className={`relative inline-flex items-center ${knowledgeBaseActive ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
											<input
												type="checkbox"
												name="TTS_AS_TOOL"
												checked={knowledgeBaseActive ? false : settings.TTS_AS_TOOL}
												onChange={onSettingsChange}
												disabled={knowledgeBaseActive}
												className="sr-only peer"
											/>
											<div className="w-11 h-6 bg-grayLight peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emeraldLight/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emeraldDeep peer-disabled:opacity-50"></div>
										</label>
									</div>

									{knowledgeBaseActive && (
										<div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
											<AlertTriangle className="w-4 h-4 flex-shrink-0" />
											<span>Áudio como Ferramenta temporariamente desabilitado enquanto a Base de Conhecimento está ativa (limitação da API Gemini)</span>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TTSSection;
